import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { CartItem } from './types';

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartItem[]>([]);

  const uniqueItemCount = computed(() => items.value.length);

  const itemCount = computed(() => {
    return items.value.reduce((acc, item) => acc + item.quantity, 0);
  });

  function addItem(item: CartItem) {
    const existingItem = findItem(item.id);
    if (existingItem) {
      // Ensure quantity is a multiple of the quantity step
      if (item.quantityStep) {
        existingItem.quantity =
          Math.floor((existingItem.quantity + item.quantity) / item.quantityStep) *
          item.quantityStep;
      } else {
        existingItem.quantity += item.quantity;
      }
      return;
    }

    items.value.push(item);
  }

  function removeItem(itemId: string) {
    items.value = items.value.filter((item) => item.id !== itemId);
  }

  function updateItemQuantity(itemId: string, quantity: number) {
    const item = findItem(itemId);
    if (item) {
      if (quantity > 0) {
        // Ensure quantity is a multiple of the quantity step
        if (item.quantityStep) {
          item.quantity = Math.floor(quantity / item.quantityStep) * item.quantityStep;
        } else {
          item.quantity = quantity;
        }
      } else {
        removeItem(itemId);
      }
    }
  }

  function clearCart() {
    items.value = [];
  }

  function findItem(itemId: string) {
    return items.value.find((item) => item.id === itemId);
  }

  return {
    items,
    itemCount,
    uniqueItemCount,
    findItem,
    addItem,
    removeItem,
    updateItemQuantity,
    clearCart,
  };
});
